import { JUZ_CREDIT, QURAN_CREDIT, SOURCE_HOMES, kemenagUrl } from '@iqrapath/shared'
import { useQueryClient } from '@tanstack/react-query'
import { BookOpenText, ChevronRight, CornerDownRight, Flag, Search, Trash2, X } from 'lucide-react'
import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { EmptyState } from '../../components/EmptyState.tsx'
import { Obj } from '../../components/Obj.tsx'
import { SkRows } from '../../components/Skeleton.tsx'
import { SourceLink } from '../../components/SourceLink.tsx'
import { useToast } from '../../lib/toast.ts'
import {
  POPULAR, norm, parseRef, rankSurahs, recentSurahs, searchable, setReadingMark, toggleTanda, useAyatSearch, useJuz, useReading, useSurahs, useTanda,
  type SurahInfo, type Tanda,
} from './quran.ts'

type Tab = 'surat' | 'juz' | 'tanda'
const TABS: { id: Tab; label: string }[] = [{ id: 'surat', label: 'Surat' }, { id: 'juz', label: 'Juz' }, { id: 'tanda', label: 'Tanda' }]
const UNDO_MS = 6000

const verseUrl = (surah: number, ayah: number | null) => (ayah ? `/quran/${surah}?ayat=${ayah}` : `/quran/${surah}`)

function useDebounced<T>(v: T, ms = 300) {
  const [d, setD] = useState(v)
  useEffect(() => {
    const t = setTimeout(() => setD(v), ms)
    return () => clearTimeout(t)
  }, [v, ms])
  return d
}

function SurahRow({ s }: { s: SurahInfo }) {
  return (
    <li>
      <Link to={`/quran/${s.n}`} className="surah-row">
        <span className="sn num" aria-hidden="true">{s.n}</span>
        <span className="st">
          <b>{s.nama}</b>{' '}
          <small>{s.arti} · {s.tempat} · {s.jumlah} ayat</small>
        </span>
        <span className="ar sa" lang="ar" aria-hidden="true">{s.namaAr}</span>
      </Link>
    </li>
  )
}

/**
 * "Lanjut baca": the verse marked with "Tandai terakhir baca" in the reader, with how far into the surah it is, and a way
 * to remove the marker. Without a marker: how to set one.
 */
function ContinueCard({ surahs }: { surahs: SurahInfo[] }) {
  const { data: reading } = useReading()
  const qc = useQueryClient()
  const [toast, show] = useToast()
  const last = reading && surahs.find((s) => s.n === reading.surah)
  const clear = async () => {
    const err = await setReadingMark(qc, null)
    show(err ? 'Belum bisa dihapus. Cek koneksi, lalu coba lagi.' : 'Penanda terakhir baca dihapus.')
  }
  return (
    <>
      {reading && last ? (
        <div className="cont-wrap reveal">
          <Link className="card continue" to={verseUrl(last.n, reading.ayah)}>
            <span className="ic obj-ic" aria-hidden="true"><Obj id="mushaf" /></span>
            <div className="cont-t">
              <small>Lanjut baca</small>{' '}
              <b>{last.nama} · ayat {reading.ayah}</b>{' '}
              <span className="cont-bar" aria-hidden="true"><i style={{ width: `${Math.max(2, Math.round((reading.ayah / last.jumlah) * 100))}%` }} /></span>
              <span className="cont-sub">dari {last.jumlah} ayat</span>
            </div>
            <ChevronRight className="chev" size={18} aria-hidden="true" />
          </Link>
          <button type="button" className="link-btn cont-clear" onClick={() => void clear()}>Hapus penanda</button>
        </div>
      ) : (
        <p className="card cont-hint reveal">
          <Flag size={16} aria-hidden="true" />
          <span>Saat membaca, tekan ikon bendera di ayat terakhir yang kamu baca. Penandanya muncul di sini sebagai “Lanjut baca”.</span>
        </p>
      )}
      {toast && <div className="toast-slot" role="status"><p className="toast">{toast}</p></div>}
    </>
  )
}

function SurahTab({ surahs }: { surahs: SurahInfo[] }) {
  const recent = useMemo(() => recentSurahs().map((n) => surahs.find((s) => s.n === n)).filter((s): s is SurahInfo => Boolean(s)), [surahs])
  const popular = POPULAR.map((n) => surahs.find((s) => s.n === n)).filter((s): s is SurahInfo => Boolean(s))
  return (
    <>
      <ContinueCard surahs={surahs} />
      {recent.length > 0 && (
        <>
          <h2 className="section-t">Baru dibuka</h2>
          <nav className="rasa" aria-label="Baru dibuka">{recent.map((s) => <Link key={s.n} to={`/quran/${s.n}`}>{s.nama}</Link>)}</nav>
        </>
      )}
      <h2 className="section-t">Sering dibaca</h2>
      <nav className="rasa popular" aria-label="Surat populer">{popular.map((s) => <Link key={s.n} to={`/quran/${s.n}`}>{s.nama}</Link>)}</nav>
      <h2 className="section-t">Semua surat</h2>
      <ol className="card surah-list" aria-label="Daftar surat">{surahs.map((s) => <SurahRow key={s.n} s={s} />)}</ol>
    </>
  )
}

function JuzTab({ surahs }: { surahs: SurahInfo[] }) {
  const { data: juz } = useJuz()
  if (!juz?.length) return <p className="muted q-note">Pembagian juz belum terpasang di server.</p>
  return (
    <>
      <ol className="card surah-list" aria-label="Daftar juz">
        {juz.map((j) => {
          const s = surahs.find((x) => x.n === j.surah)
          return (
            <li key={j.n}>
              <Link to={verseUrl(j.surah, j.ayah)} className="surah-row">
                <span className="sn num" aria-hidden="true">{j.n}</span>
                <span className="st"><b>Juz {j.n}</b> <small>Mulai {s?.nama ?? `surat ${j.surah}`} ayat {j.ayah}</small></span>
                <ChevronRight className="chev" size={18} aria-hidden="true" />
              </Link>
            </li>
          )
        })}
      </ol>
      <p className="credit q-credit"><SourceLink href={SOURCE_HOMES.qurancom}>{JUZ_CREDIT}</SourceLink></p>
    </>
  )
}

/** Bookmarked verses; removing waits a few seconds for "Urungkan" (same as the Kotak, UX-PLAYBOOK §10). */
function TandaTab() {
  const { data, isPending, isError, refetch } = useTanda()
  const qc = useQueryClient()
  const [toast, show] = useToast()
  const [hidden, setHidden] = useState<Set<string>>(new Set())
  const [undo, setUndo] = useState<Tanda | null>(null)
  const pending = useRef<Tanda | null>(null)
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined)
  const key = (t: Tanda) => `${t.surah}:${t.ayah}`
  const unhide = (k: string) => setHidden((s) => { const n = new Set(s); n.delete(k); return n })

  const commit = async () => {
    const t = pending.current
    if (!t) return
    clearTimeout(timer.current)
    pending.current = null
    setUndo(null)
    const err = await toggleTanda(qc, t, false)
    unhide(key(t))
    if (err) show('Tanda belum bisa dihapus. Cek koneksi, lalu coba lagi.')
  }
  const remove = (t: Tanda) => {
    void commit()
    pending.current = t
    setHidden((s) => new Set(s).add(key(t)))
    setUndo(t)
    timer.current = setTimeout(() => void commit(), UNDO_MS)
  }
  const restore = () => {
    clearTimeout(timer.current)
    if (pending.current) unhide(key(pending.current))
    pending.current = null
    setUndo(null)
  }
  // leaving the tab within the undo window still removes it
  useEffect(() => () => { void commit() }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const items = data?.filter((t) => !hidden.has(key(t))) ?? []
  return (
    <>
      {isPending && <SkRows label="Memuat tanda" rows={4} />}
      {isError && (
        <EmptyState Icon={BookOpenText} title="Belum bisa dimuat" action={<button className="btn btn-primary" onClick={() => refetch()}>Coba lagi</button>}>
          Koneksi lagi bermasalah. Cek internet kamu, lalu coba lagi.
        </EmptyState>
      )}
      {data && items.length === 0 && (
        <EmptyState obj="mushaf" title="Belum ada ayat yang ditandai">Saat membaca, tekan ikon penanda di samping ayat. Ayatnya akan tersimpan di sini.</EmptyState>
      )}
      {items.length > 0 && (
        <ol className="card surah-list" aria-label="Ayat yang ditandai">
          {items.map((t) => (
            <li key={key(t)} className="tanda-row">
              <Link to={verseUrl(t.surah, t.ayah)} className="hit-row">
                <span className="pill pill-mint num">{t.surah}:{t.ayah}</span>
                <span className="st"><b>{t.nama ?? `Surat ${t.surah}`} · ayat {t.ayah}</b>{' '}{t.snippet && <small>{t.snippet}</small>}</span>
              </Link>
              <button type="button" className="icon-btn plain" onClick={() => remove(t)} aria-label={`Hapus tanda ${t.nama ?? t.surah} ayat ${t.ayah}`}>
                <Trash2 size={18} aria-hidden="true" />
              </button>
            </li>
          ))}
        </ol>
      )}
      <div className="toast-slot" role="status">
        {undo ? <p className="toast kt-undo">Tanda dihapus. <button type="button" onClick={restore}>Urungkan</button></p> : toast && <p className="toast">{toast}</p>}
      </div>
    </>
  )
}

function Mark({ text, match }: { text: string; match: [number, number] }) {
  return <>{text.slice(0, match[0])}<mark>{text.slice(match[0], match[1])}</mark>{text.slice(match[1])}</>
}

/** Results while typing: a direct reference ("2:255"), matching surahs, then verses whose translation has the words. */
function Results({ q, surahs }: { q: string; surahs: SurahInfo[] }) {
  const ref = parseRef(q, surahs)
  const named = ref?.ayah ? [] : rankSurahs(surahs, q).filter((s) => s.n !== ref?.surah.n)
  const dq = useDebounced(q.trim())
  const numeric = /^[\d\s:./]+$/.test(dq)
  const words = searchable(dq) && !numeric && !parseRef(dq, surahs)?.ayah ? dq : ''
  const s = useAyatSearch(words)
  const pages = s.data?.pages ?? []
  const hits = pages.flatMap((p) => p.hits)
  const total = pages[0]?.total ?? 0
  const waiting = words !== '' && (s.isPending || s.isPlaceholderData || dq !== q.trim())
  const nothing = !ref && named.length === 0 && (!words || (!waiting && !s.isError && total === 0))

  return (
    <div className="q-results">
      {ref && (
        <Link className="card row-card ref-hit" to={verseUrl(ref.surah.n, ref.ayah)}>
          <span className="ic" aria-hidden="true"><CornerDownRight size={20} /></span>
          <div>
            <small>Buka langsung</small>{' '}
            <b>QS {ref.surah.nama}{ref.ayah ? `: ${ref.ayah}` : ''}</b>{' '}
            {ref.tooFar && <small>Surat ini {ref.surah.jumlah} ayat, jadi dibuka di ayat terakhir.</small>}
          </div>
          <ChevronRight className="chev" size={18} aria-hidden="true" />
        </Link>
      )}

      {named.length > 0 && (
        <>
          <h2 className="section-t">Surat</h2>
          <ol className="card surah-list" aria-label="Surat yang cocok">{named.map((x) => <SurahRow key={x.n} s={x} />)}</ol>
        </>
      )}

      {words && (
        <section aria-label="Hasil di terjemahan">
          <h2 className="section-t">Di terjemahan{total > 0 && !waiting ? ` · ${total} ayat` : ''}</h2>
          {waiting && hits.length === 0 && <SkRows label="Mencari di terjemahan" rows={3} />}
          {s.isError && <p className="muted q-note">Belum bisa mencari. Cek koneksi, lalu coba lagi.</p>}
          {hits.length > 0 && (
            <>
              <ol className={`card surah-list${waiting ? ' stale' : ''}`} aria-label="Ayat yang cocok">
                {hits.map((h) => (
                  <li key={`${h.surah}:${h.ayah}`}>
                    <Link to={verseUrl(h.surah, h.ayah)} className="hit-row">
                      <span className="pill pill-mint num">{h.surah}:{h.ayah}</span>
                      <span className="st"><b>{h.nama}</b>{' '}<small><Mark text={h.snippet} match={h.match} /></small></span>
                    </Link>
                  </li>
                ))}
              </ol>
              {s.hasNextPage && (
                <button type="button" className="btn btn-ghost btn-block load-more" onClick={() => s.fetchNextPage()} disabled={s.isFetchingNextPage}>
                  {s.isFetchingNextPage ? 'Memuat…' : `Tampilkan lebih banyak (${hits.length} dari ${total})`}
                </button>
              )}
              <p className="credit q-credit"><SourceLink href={kemenagUrl(hits[0]!.surah, hits[0]!.ayah)}>{QURAN_CREDIT}</SourceLink></p>
            </>
          )}
        </section>
      )}

      {!words && !ref && norm(q).length > 0 && norm(q).length < 3 && named.length === 0 && (
        <p className="muted q-note">Ketik minimal 3 huruf untuk mencari di terjemahan.</p>
      )}
      {nothing && norm(q).length >= 3 && (
        <p className="muted q-note">Tidak ada yang cocok dengan “{q.trim()}”. Coba nama surat, nomor seperti 2:255, atau kata lain.</p>
      )}
    </div>
  )
}

/**
 * /quran — find a place in the Qur'an (docs/PRD.md US-5.1): the search box and the Surat · Juz · Tanda switch stay at
 * the top while the list scrolls. One box understands surah names, numbers ("36"), references ("2:255", "baqarah 255")
 * and words of the translation. Query and tab live in the URL, so Back from a verse returns to the same results.
 */
export function QuranHome() {
  const { data: surahs, isPending, isError, refetch } = useSurahs()
  const { data: tanda } = useTanda()
  const [params, setParams] = useSearchParams()
  const navigate = useNavigate()
  // The box keeps its own state (typing is never slowed by a URL update); the URL follows, so Back restores it.
  const [q, setQ] = useState(() => params.get('q') ?? '')
  const tab: Tab = params.get('tab') === 'juz' ? 'juz' : params.get('tab') === 'tanda' ? 'tanda' : 'surat'

  const setParam = (key: string, value: string | null) =>
    setParams((prev) => {
      const p = new URLSearchParams(prev)
      if (value) p.set(key, value); else p.delete(key)
      return p
    }, { replace: true })
  useEffect(() => {
    if ((params.get('q') ?? '') !== q) setParam('q', q || null)
  }, [q]) // eslint-disable-line react-hooks/exhaustive-deps
  const update = (next: { q?: string; tab?: Tab }) => {
    if (next.q !== undefined) setQ(next.q)
    if (next.tab) setParam('tab', next.tab === 'surat' ? null : next.tab)
  }
  const submit = (e: FormEvent) => {
    e.preventDefault()
    const ref = surahs && parseRef(q, surahs)
    if (ref) navigate(verseUrl(ref.surah.n, ref.ayah))
  }

  return (
    <div className="page quran-home">
      <div className="q-top">
        <h1>Al-Qur’an</h1>
        <form className="search" role="search" onSubmit={submit}>
          <Search size={18} aria-hidden="true" />
          <input
            type="search" className="input" enterKeyHint="search" autoComplete="off"
            placeholder="Nama surat, 2:255, atau kata" aria-label="Cari surat atau ayat" aria-describedby="q-hint"
            value={q} onChange={(e) => update({ q: e.target.value })}
          />
          {q && <button type="button" className="search-clear" aria-label="Hapus pencarian" onClick={() => update({ q: '' })}><X size={18} /></button>}
        </form>
        <p id="q-hint" className="sr">Bisa nama surat, nomor surat, rujukan seperti 2:255 atau baqarah 255, atau kata dalam terjemahan.</p>
        {!q && (
          <div className="seg q-tabs" role="tablist" aria-label="Tampilan">
            {TABS.map((t) => (
              <button key={t.id} type="button" role="tab" id={`qt-${t.id}`} aria-selected={tab === t.id} aria-controls="q-panel" onClick={() => update({ tab: t.id })}>
                {t.label}{t.id === 'tanda' && tanda?.length ? <>{' '}<span className="q-count num">{tanda.length}</span></> : null}
              </button>
            ))}
          </div>
        )}
      </div>

      {isPending && <SkRows label="Memuat daftar surat" className="surah-list" />}
      {isError && (
        <EmptyState Icon={BookOpenText} title="Belum bisa dimuat" action={<button className="btn btn-primary" onClick={() => refetch()}>Coba lagi</button>}>
          Koneksi lagi bermasalah. Cek internet kamu, lalu coba lagi.
        </EmptyState>
      )}
      {surahs && surahs.length === 0 && (
        <EmptyState obj="mushaf" title="Data Al-Qur’an belum terpasang">Daftar surat akan muncul di sini setelah datanya dipasang di server.</EmptyState>
      )}

      {surahs && surahs.length > 0 && (
        q ? <Results q={q} surahs={surahs} /> : (
          <div id="q-panel" role="tabpanel" aria-labelledby={`qt-${tab}`}>
            {tab === 'surat' && <SurahTab surahs={surahs} />}
            {tab === 'juz' && <JuzTab surahs={surahs} />}
            {tab === 'tanda' && <TandaTab />}
            {tab === 'surat' && <p className="credit q-credit"><SourceLink href={SOURCE_HOMES.kemenag}>{QURAN_CREDIT}</SourceLink></p>}
          </div>
        )
      )}
    </div>
  )
}
